/**
 * SurveyContainer
 * Main Container for Patient Survey using unstated-next
 *
 * This container centralizes all survey-related state management,
 * eliminating props drilling and providing a clean API for components.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createContainer } from 'unstated-next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { first, last, isEmpty, size, union, difference, toNumber } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { getPatientBasePath } from '../../../../app/route-utils';
import { usePatientLocale } from '../../../hooks/use-patient-locale';

// V2 API hooks
import {
  usePatientVisitControllerUpdate,
  usePatientVisitControllerCreate,
  usePatientVisitControllerMyVisit,
} from '../../../api/generated/patient-visits/patient-visits';
import {
  useQuestionTreeControllerFindByTreeKey,
  useQuestionTreeControllerSearch,
} from '../../../api/generated/question-trees/question-trees';
import { customInstance } from '../../../api/custom-instance';

// Feature hooks
import { useAthenaIntegration } from './hooks/useAthenaIntegration';
import { useDescriptiveNote } from './hooks/useDescriptiveNote';
import { useSurveyNavigation } from './hooks/useSurveyNavigation';
import { PaDataRepoState } from '../../../../report/use-data';
import { mapDefaultLanguageToLocaleKey } from './locale';
import { qsIncludesGlobal as reportQsIncludesGlobal } from '../../../../report/match-utils';
import { conditionHelpers, matchAgeRange } from '../../../../report/helper-functions';
import type { ShowConditionDto } from '../../../api/generated/types';
import { buildVisitRequestKey, shouldRefreshVisitBeforeMutation } from './visitSync';

// Types
import type {
  SurveyContainerReturn,
  SurveyStateType,
  ReportStateType,
  ReportErrorStateType,
  DataStateType,
  SurveyParams,
  VisitType,
  SurveyType,
  OrganizationType,
  ErrorInfo,
  PatientDetails,
  defaultReportState,
  defaultSurveyState,
  defaultErrorState,
  defaultDataState,
} from './types';

// Initial state values
const initialReportState: ReportStateType = {
  globalAnswerMap: {},
  treeStates: [],
  nextTreeKeys: [],
  presentIllness: [],
  acceptedTerms: false,
  systemValueMap: {},
};

const initialSurveyState: SurveyStateType = {
  adaptedTreeKeys: [],
};

const initialErrorState: ReportErrorStateType = {
  maxErrorsReached: false,
  unhandledErrors: [],
};

const initialDataState: DataStateType = {
  errors: [],
};

const DATA_STATE_STORAGE_KEY = 'PaDataStateV2';

/**
 * Helper function to get survey params from URL
 */
function useSurveyParams(): SurveyParams {
  const params = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  return {
    orgId: params.orgId,
    shortId: params.shortId,
    treeKey: params.treeKey,
    referralMessageId: searchParams.get('referralMessageId') || undefined,
  };
}

/**
 * Helper function to get aid from URL
 */
function useAidFromUrl(): string | null {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get('aid');
}

/**
 * Main Survey Container Hook
 */
function useSurveyContainer(): SurveyContainerReturn {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const patientLocale = usePatientLocale();
  const params = useSurveyParams();
  const urlAid = useAidFromUrl();
  const { orgId, shortId, treeKey } = params;
  const organizationId = Number(orgId || 0);
  const patientBasePath = getPatientBasePath(location.pathname);

  // ==================== STATE ====================

  // Report State
  const [reportState, setReportState] = useState<ReportStateType>(() => {
    // Try to restore from localStorage
    try {
      const stored = localStorage.getItem('PaReportState');
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...initialReportState, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to restore report state:', e);
    }
    return initialReportState;
  });

  // Survey State
  const [surveyState, setSurveyState] = useState<SurveyStateType>(() => {
    try {
      const stored = localStorage.getItem('PaSurveyState');
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...initialSurveyState, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to restore survey state:', e);
    }
    return initialSurveyState;
  });

  // Error State
  const [errorState, setErrorState] = useState<ReportErrorStateType>(initialErrorState);

  // Data State (populated from API)
  const [dataState, setDataState] = useState<DataStateType>(() => {
    try {
      const stored = localStorage.getItem(DATA_STATE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // shortId 불일치 → 다른 설문의 stale 데이터 → 폐기
        const storedShortId = parsed.survey?.shortId;
        if (storedShortId && shortId && storedShortId !== shortId) {
          localStorage.removeItem(DATA_STATE_STORAGE_KEY);
          localStorage.removeItem('PaReportState');
          localStorage.removeItem('PaSurveyState');
          return initialDataState;
        }
        return { ...initialDataState, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to restore data state:', e);
    }
    return initialDataState;
  });
  const setPaDataRepoState = useSetRecoilState(PaDataRepoState);

  // Current AID
  const [currentAid, setCurrentAid] = useState<string | null>(() => {
    return localStorage.getItem('currentAid');
  });
  const visitRequestKey = useMemo(
    () =>
      buildVisitRequestKey({
        shortId,
        referralMessageId: params.referralMessageId,
        appointmentId: urlAid,
      }),
    [params.referralMessageId, shortId, urlAid],
  );
  const [lastSyncedVisitRequestKey, setLastSyncedVisitRequestKey] = useState<string | null>(null);
  const lastRequestedVisitRequestKeyRef = useRef<string | null>(null);

  // Stage Boundary Tree Keys (for calculating stage progress - only trees with stageBoundary=true)
  const [stageBoundaryTreeKeys, setStageBoundaryTreeKeys] = useState<string[]>([]);
  const [enabledTreeKeys, setEnabledTreeKeys] = useState<string[]>([]);
  const [treeShowConditionsMap, setTreeShowConditionsMap] = useState<Record<string, ShowConditionDto[]>>({});
  const [treeMetadataLoaded, setTreeMetadataLoaded] = useState(false);

  // ==================== API QUERIES ====================

  // Search for visit by UUID or shortId
  const {
    mutate: fetchMyVisit,
    mutateAsync: fetchMyVisitAsync,
    data: myVisitData,
    isPending: isVisitLoading,
  } = usePatientVisitControllerMyVisit();

  // Update visit mutation
  const updateVisitMutation = usePatientVisitControllerUpdate();

  // Create visit mutation
  const createVisitMutation = usePatientVisitControllerCreate();

  const surveyIdForTreeQuery = Number(dataState.survey?.id || 0);
  const { data: treeByKeyData } = useQuestionTreeControllerFindByTreeKey(
    {
      surveyId: surveyIdForTreeQuery,
      treeKey: treeKey || '',
    },
    {
      query: {
        enabled: surveyIdForTreeQuery > 0 && !!treeKey,
      },
    },
  );

  const { data: treeSearchResultsData } = useQuestionTreeControllerSearch(
    {
      surveyId: surveyIdForTreeQuery,
      treeKey: treeKey || '',
    },
    {
      query: {
        enabled: surveyIdForTreeQuery > 0 && !!treeKey,
      },
    },
  );

  const updateDataState = useCallback((updater: (prev: DataStateType) => DataStateType) => {
    setDataState((prev) => {
      const next = updater(prev);
      try {
        localStorage.setItem(DATA_STATE_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn('Failed to persist data state:', e);
      }
      return next;
    });
  }, []);

  const applyUpdatedVisitToState = useCallback((updatedVisit: any) => {
    if (!updatedVisit) return;

    updateDataState((prev) => ({
      ...prev,
      visit: {
        ...(prev.visit || {}),
        ...updatedVisit,
      },
    }));
  }, [updateDataState]);

  const refreshVisitFromServer = useCallback(async () => {
    if (!shortId) {
      return null;
    }

    try {
      const response = await fetchMyVisitAsync({
        data: {
          shortId,
          referralMessageId: params.referralMessageId,
          appointmentId: urlAid || undefined,
          createIfMissing: true,
        },
      });

      const resolved = response as any;
      if (!resolved?.visit && !resolved?.survey && !resolved?.org) {
        return null;
      }

      updateDataState((prev) => ({
        ...prev,
        visit: resolved.visit || prev.visit,
        survey: resolved.survey || prev.survey,
        org: resolved.org || prev.org,
      }));
      lastRequestedVisitRequestKeyRef.current = visitRequestKey;
      setLastSyncedVisitRequestKey(visitRequestKey);

      return resolved.visit || null;
    } catch (error) {
      console.error('Failed to refresh visit from server:', error);
      return null;
    }
  }, [fetchMyVisitAsync, params.referralMessageId, shortId, updateDataState, urlAid, visitRequestKey]);

  // ==================== DERIVED STATE ====================

  // Current visit
  const visit = useMemo<VisitType | undefined>(() => {
    const v = (myVisitData as any)?.visit || dataState.visit;
    if (v) {
      return {
        id: v.id,
        uuid: v.uuid,
        status: v.status as VisitType['status'],
        archived: v.archived,
        visitedAt: v.visitedAt,
        createdAt: v.createdAt,
        updatedAt: v.updatedAt,
        surveyState: v.surveyState,
      };
    }
    return undefined;
  }, [myVisitData, dataState.visit]);

  // Survey (from visit or fallback)
  const survey = useMemo<SurveyType | undefined>(() => {
    // In V2, survey data should come from the visit or a separate survey query
    // For now, return from dataState if available
    return dataState.survey;
  }, [dataState.survey]);

  // Organization
  const org = useMemo<OrganizationType | undefined>(() => {
    return dataState.org;
  }, [dataState.org]);

  const [defaultLanguage, setDefaultLanguage] = useState('en');

  // Initialize visit and survey via my-visit endpoint
  useEffect(() => {
    if (!shortId) {
      return;
    }
    if (lastRequestedVisitRequestKeyRef.current === visitRequestKey) {
      return;
    }
    lastRequestedVisitRequestKeyRef.current = visitRequestKey;
    setLastSyncedVisitRequestKey(null);

    fetchMyVisit({
      data: {
        shortId,
        referralMessageId: params.referralMessageId,
        appointmentId: urlAid || undefined,
        createIfMissing: true,
      },
    });
  }, [fetchMyVisit, params.referralMessageId, shortId, urlAid, visitRequestKey]);

  useEffect(() => {
    const response = myVisitData as any;
    if (!response?.visit && !response?.survey) {
      return;
    }

    updateDataState((prev) => ({
      ...prev,
      visit: response.visit || prev.visit,
      survey: response.survey || prev.survey,
      org: response.org || prev.org,
    }));
    setLastSyncedVisitRequestKey(visitRequestKey);
  }, [myVisitData, updateDataState, visitRequestKey]);

  useEffect(() => {
    let isActive = true;
    const fetchDefaultLanguage = async () => {
      if (!organizationId) {
        return;
      }
      try {
        const result = await customInstance<{ value?: string }>({
          method: 'GET',
          url: `/config/public/default-language/${organizationId}`,
        });
        if (isActive) {
          setDefaultLanguage(result?.value || 'en');
        }
      } catch (error) {
        console.warn('Failed to load default language:', error);
        if (isActive) {
          setDefaultLanguage('en');
        }
      }
    };

    fetchDefaultLanguage();
    return () => {
      isActive = false;
    };
  }, [organizationId]);

  useEffect(() => {
    const localeFromDefault = mapDefaultLanguageToLocaleKey(defaultLanguage);

    if (patientLocale.userLocaleKey) {
      return;
    }

    if (patientLocale.localeKey === localeFromDefault) {
      return;
    }

    patientLocale.setLocale(localeFromDefault);
  }, [
    patientLocale.localeKey,
    patientLocale.userLocaleKey,
    defaultLanguage,
    patientLocale.setLocale,
  ]);

  useEffect(() => {
    if (!dataState.survey && !dataState.org && !dataState.visit) {
      return;
    }

    setPaDataRepoState((prev) =>
      produce(prev, (draft) => {
        if (dataState.survey) {
          draft.survey = dataState.survey as any;
        }
        if (dataState.org) {
          draft.org = dataState.org as any;
        }
        if (dataState.visit) {
          draft.visit = dataState.visit as any;
        }
      }),
    );
  }, [dataState.survey, dataState.org, dataState.visit, setPaDataRepoState]);

  // Clear translations when surveyId changes (prevents cross-survey translation contamination)
  const prevSurveyIdRef = useRef<number | null>(null);

  useEffect(() => {
    const currentSurveyId = dataState.survey?.id ? Number(dataState.survey.id) : null;
    if (
      prevSurveyIdRef.current !== null &&
      currentSurveyId !== null &&
      prevSurveyIdRef.current !== currentSurveyId
    ) {
      setPaDataRepoState((prev) =>
        produce(prev, (draft) => {
          draft.translations = [];
          draft.doctorsTerms = [];
        }),
      );
    }
    if (currentSurveyId !== null) {
      prevSurveyIdRef.current = currentSurveyId;
    }
  }, [dataState.survey?.id, setPaDataRepoState]);

  // Survey ID
  const surveyId = survey?.id;
  const currentTreeState = useMemo(() => {
    const exact = reportState.treeStates.find((treeStateItem) => treeStateItem.treeKey === surveyState.curTreeKey);
    return exact || first(reportState.treeStates);
  }, [reportState.treeStates, surveyState.curTreeKey]);

  // Fetch tree metadata (enabled/stageBoundary) when survey is loaded
  useEffect(() => {
    let isActive = true;
    const fetchTreeMetadata = async () => {
      if (!surveyId || !survey?.questionTreeKeys?.length) {
        setStageBoundaryTreeKeys([]);
        setEnabledTreeKeys([]);
        setTreeMetadataLoaded(false);
        return;
      }

      try {
        const result = await customInstance<Array<{
          treeKey: string;
          stageBoundary?: boolean;
          enabled?: boolean;
          showConditions?: ShowConditionDto[] | null;
        }>>({
          method: 'POST',
          url: '/question-trees/find',
          data: {
            surveyId: Number(surveyId),
            treeKeys: survey.questionTreeKeys,
          },
        });

        if (isActive && result) {
          const showConditionsByTreeKey = result.reduce<Record<string, ShowConditionDto[]>>((acc, tree) => {
            const rawShowConditions = tree.showConditions;
            acc[tree.treeKey] = Array.isArray(rawShowConditions) ? rawShowConditions : [];
            return acc;
          }, {});
          setTreeShowConditionsMap(showConditionsByTreeKey);

          // Keep original order from survey.questionTreeKeys
          const filteredEnabledKeys = survey.questionTreeKeys.filter((key) => {
            const tree = result.find((t) => t.treeKey === key);
            return tree ? tree.enabled !== false : true;
          });
          setEnabledTreeKeys(filteredEnabledKeys);

          const boundaryKeys = filteredEnabledKeys.filter((key) => {
            const tree = result.find((t) => t.treeKey === key);
            return tree?.stageBoundary === true;
          });
          setStageBoundaryTreeKeys(boundaryKeys);
          setTreeMetadataLoaded(true);
        }
      } catch (error) {
        console.warn('Failed to load stage boundary trees:', error);
        if (isActive) {
          setStageBoundaryTreeKeys([]);
          setEnabledTreeKeys([]);
          setTreeShowConditionsMap({});
          setTreeMetadataLoaded(false);
        }
      }
    };

    fetchTreeMetadata();
    return () => {
      isActive = false;
    };
  }, [surveyId, survey?.questionTreeKeys]);

  const isTreeConditionMet = useCallback((condition: ShowConditionDto) => {
    const { operator, qsKey, value, func, category, ageMin, ageMax, ageUnit, type } = condition;

    if (operator != null) {
      const firstValue = first(currentTreeState?.answerMap?.[qsKey || '']?.answerValues);
      if (firstValue != null) {
        const lhs = toNumber(firstValue);
        const rhs = toNumber(value);
        if (operator === 'lte' && lhs <= rhs) return true;
        if (operator === 'lt' && lhs < rhs) return true;
        if (operator === 'gte' && lhs >= rhs) return true;
        if (operator === 'gt' && lhs > rhs) return true;
        if (operator === 'eq' && lhs === rhs) return true;
        if (operator === 'ne' && lhs !== rhs) return true;
      }
    }

    if (func) {
      const helper = conditionHelpers[func as keyof typeof conditionHelpers];
      if (helper) {
        return helper({
          question: undefined as any,
          treeState: currentTreeState as any,
          reportState: reportState as any,
          survey: survey as any,
          condition: condition as any,
        });
      }
    }

    if (category === 'age-range') {
      return matchAgeRange({
        reportState: reportState as any,
        ageMin,
        ageMax,
        ageUnit: ageUnit as any,
      });
    }

    if (type === 'include') {
      return reportQsIncludesGlobal({
        condition: condition as any,
        question: undefined,
        treeState: currentTreeState as any,
        treeStates: reportState.treeStates as any,
      });
    }

    return false;
  }, [currentTreeState, reportState, survey]);

  const effectiveQuestionTreeKeys = useMemo(() => {
    if (treeMetadataLoaded) {
      return enabledTreeKeys.filter((treeKeyItem) => {
        const showConditions = treeShowConditionsMap[treeKeyItem] || [];
        if (showConditions.length === 0) return true;

        const andConditions = showConditions.filter((cond) => cond.logicType === 'AND');
        const orConditions = showConditions.filter((cond) => cond.logicType === 'OR' || !cond.logicType);

        const andMet = andConditions.length === 0 || andConditions.every((condition) => isTreeConditionMet(condition));
        const orMet = orConditions.length === 0 || orConditions.some((condition) => isTreeConditionMet(condition));

        return andMet && orMet;
      });
    }
    return survey?.questionTreeKeys || [];
  }, [treeMetadataLoaded, enabledTreeKeys, treeShowConditionsMap, isTreeConditionMet, survey?.questionTreeKeys]);

  const effectiveSurvey = useMemo<SurveyType | undefined>(() => {
    if (!survey) return undefined;
    return {
      ...survey,
      questionTreeKeys: effectiveQuestionTreeKeys,
    };
  }, [survey, effectiveQuestionTreeKeys]);

  // Current tree index
  const curTreeIdx = useMemo(() => {
    const questionTreeKeys = effectiveQuestionTreeKeys;
    return questionTreeKeys.findIndex((k) => k === surveyState.curTreeKey);
  }, [effectiveQuestionTreeKeys, surveyState.curTreeKey]);

  // Is last tree
  const isLastTree = useMemo(() => {
    const questionTreeKeys = effectiveQuestionTreeKeys;
    return !isEmpty(questionTreeKeys) && last(questionTreeKeys) === surveyState.curTreeKey;
  }, [effectiveQuestionTreeKeys, surveyState.curTreeKey]);

  // Patient details
  const { patientDetails } = surveyState;

  // ==================== STATE UPDATERS ====================

  // Update report state with Immer
  const updateReportState = useCallback((updater: (state: ReportStateType) => ReportStateType) => {
    setReportState((prev) => {
      const next = updater(prev);
      // Persist to localStorage
      try {
        localStorage.setItem('PaReportState', JSON.stringify(next));
      } catch (e) {
        console.warn('Failed to persist report state:', e);
      }
      return next;
    });
  }, []);

  // Update survey state with Immer
  const updateSurveyState = useCallback((updater: (state: SurveyStateType) => SurveyStateType) => {
    setSurveyState((prev) => {
      const next = updater(prev);
      // Persist to localStorage
      try {
        localStorage.setItem('PaSurveyState', JSON.stringify(next));
      } catch (e) {
        console.warn('Failed to persist survey state:', e);
      }
      return next;
    });
  }, []);

  // ==================== VISIT UPDATE ====================

  /**
   * Update visit with current state
   */
  const updateVisit = useCallback(
    (nState: ReportStateType, options?: { data?: Partial<VisitType> }, transitioningToTreeKey?: string | null) => {
      if (!visit?.id) {
        console.warn('No visit ID available for update');
        return;
      }

      // Calculate stage progress (only for stageBoundary trees - "Mile Markers")
      const getStageProgress = () => {
        const completedStages: string[] = [];
        let activeStage: string | null = null;

        // All tree keys that have been visited (have answers)
        const seenTreeKeys = union(
          nState.treeStates.map((i) => i.treeKey),
          transitioningToTreeKey ? [transitioningToTreeKey] : []
        );

        // Use stageBoundaryTreeKeys instead of all questionTreeKeys
        // This ensures only "Mile Marker" stages are shown in progress
        const stageKeys = stageBoundaryTreeKeys.length > 0 ? stageBoundaryTreeKeys : [];

        // Find which stage boundary trees have been reached based on question tree order
        const questionTreeKeys = effectiveQuestionTreeKeys;
        const currentTreeIdx = transitioningToTreeKey
          ? questionTreeKeys.indexOf(transitioningToTreeKey)
          : Math.max(...seenTreeKeys.map((k) => questionTreeKeys.indexOf(k)), -1);

        stageKeys.forEach((stageKey) => {
          const stageIdx = questionTreeKeys.indexOf(stageKey);
          if (stageIdx === -1) return;

          // Stage is completed if we've moved past it
          if (stageIdx < currentTreeIdx) {
            completedStages.push(stageKey);
          } else if (stageIdx === currentTreeIdx || (stageIdx > currentTreeIdx && seenTreeKeys.includes(stageKey))) {
            // Stage is active if it's current or we're on it
            if (activeStage) {
              completedStages.push(activeStage);
            }
            activeStage = stageKey;
          }
        });

        const userCompleted = options?.data?.status === 'reported';
        if (userCompleted && activeStage) {
          completedStages.push(activeStage);
          activeStage = null;
        }

        const unreachedStages = difference(stageKeys, [...completedStages, activeStage].filter(Boolean));

        return { completedStages, activeStage, unreachedStages };
      };

      const stageProgress = getStageProgress();
      const ccValue = (() => {
        const systemMap = nState.systemValueMap || {};
        const rawValue = systemMap.cc ?? systemMap.chiefComplaint;
        return typeof rawValue === 'string' ? rawValue.trim() : '';
      })();

      // Build survey state for API
      const surveyStateData = {
        reportState: {
          ...nState,
          errorState,
        },
        surveyState: {
          ...surveyState,
          ...stageProgress,
        },
      };

      const payload = {
        ...options?.data,
        ...(ccValue ? { cc: ccValue } : {}),
        surveyState: surveyStateData,
      };

      const runVisitUpdate = async () => {
        let visitIdToUpdate = Number(visit.id);

        if (
          shouldRefreshVisitBeforeMutation({
            requestKey: visitRequestKey,
            lastSyncedRequestKey: lastSyncedVisitRequestKey,
          })
        ) {
          const recoveredVisit = await refreshVisitFromServer();
          const recoveredVisitId = Number((recoveredVisit as { id?: number } | null)?.id);
          if (Number.isFinite(recoveredVisitId) && recoveredVisitId > 0) {
            visitIdToUpdate = recoveredVisitId;
          }
        }

        if (!Number.isFinite(visitIdToUpdate) || visitIdToUpdate <= 0) {
          console.error('[SurveyContainer] Failed to resolve visit id before update', {
            shortId,
            visitId: visit?.id,
            visitUuid: visit?.uuid,
            requestKey: visitRequestKey,
          });
          return;
        }

        try {
          const updatedVisit = await updateVisitMutation.mutateAsync({
            id: visitIdToUpdate,
            data: payload,
          });
          applyUpdatedVisitToState(updatedVisit);
        } catch (error) {
          const statusCode = (error as { response?: { status?: number } })?.response?.status;
          if (statusCode === 404) {
            console.warn('[SurveyContainer] updateVisit returned 404. Attempting recovery.', {
              shortId,
              visitId: visitIdToUpdate,
              visitUuid: visit?.uuid,
              requestKey: visitRequestKey,
            });
            const recoveredVisit = await refreshVisitFromServer();
            const recoveredVisitId = Number((recoveredVisit as { id?: number } | null)?.id);

            if (Number.isFinite(recoveredVisitId) && recoveredVisitId > 0) {
              try {
                const retriedVisit = await updateVisitMutation.mutateAsync({
                  id: recoveredVisitId,
                  data: payload,
                });
                applyUpdatedVisitToState(retriedVisit);
                return;
              } catch (retryError) {
                console.error('[SurveyContainer] Retry update failed after visit refresh:', retryError, {
                  shortId,
                  visitId: recoveredVisitId,
                  visitUuid: (recoveredVisit as { uuid?: string } | null)?.uuid || visit?.uuid,
                  requestKey: visitRequestKey,
                });
              }
            }
          }

          console.error('[SurveyContainer] Failed to update patient visit:', error, {
            shortId,
            visitId: visitIdToUpdate,
            visitUuid: visit?.uuid,
            requestKey: visitRequestKey,
          });
        }
      };

      runVisitUpdate();
    },
    [
      visit?.id,
      effectiveQuestionTreeKeys,
      stageBoundaryTreeKeys,
      surveyState,
      errorState,
      updateVisitMutation,
      applyUpdatedVisitToState,
      refreshVisitFromServer,
      visit?.uuid,
      shortId,
      visitRequestKey,
      lastSyncedVisitRequestKey,
    ]
  );

  // ==================== RESET STATE ====================

  /**
   * Reset all states (for new patient/session)
   */
  const resetAllStates = useCallback(() => {
    setReportState(initialReportState);
    setSurveyState(initialSurveyState);
    setErrorState(initialErrorState);
    setDataState(initialDataState);

    // PaDataRepoState 번역 데이터도 초기화
    setPaDataRepoState((prev) =>
      produce(prev, (draft) => {
        draft.translations = [];
        draft.doctorsTerms = [];
      }),
    );

    // Clear localStorage
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes('PaReportState') ||
            key.includes('PaSurveyState') ||
            key.includes(DATA_STATE_STORAGE_KEY) ||
            key.includes('PaErrorState') ||
            key.includes('AidState'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }, [setPaDataRepoState]);

  // ==================== ERROR HANDLING ====================

  /**
   * Record unhandled error
   */
  const recordUnhandledError = useCallback(
    (error: { message: ErrorInfo }) => {
      if (errorState.maxErrorsReached) {
        return;
      }

      const atMaxErrors = size(errorState.unhandledErrors) + 1 >= 20;

      setErrorState({
        ...errorState,
        maxErrorsReached: atMaxErrors,
        unhandledErrors: [
          ...errorState.unhandledErrors,
          {
            createdOn: new Date().toISOString(),
            message: error.message.message,
            source: error.message.source,
            lineInfo: error.message.lineInfo,
          },
        ],
      });
    },
    [errorState]
  );

  // ==================== FEATURE HOOKS ====================

  // Athena Integration
  const athena = useAthenaIntegration({
    surveyState,
    reportState,
    onPatientDetailsUpdate: (details: PatientDetails) => {
      updateSurveyState((state) => ({
        ...state,
        patientDetails: details,
      }));
    },
  });

  // Descriptive Note
  const descriptiveNote = useDescriptiveNote({
    reportState,
    surveyState,
    visit,
    shortId,
    defaultLanguage,
    onReportStateUpdate: updateReportState,
    onVisitNotFound: refreshVisitFromServer,
  });

  // Survey Navigation
  const navigationProps = useSurveyNavigation({
    survey: effectiveSurvey,
    surveyState,
    reportState,
    onSurveyStateUpdate: updateSurveyState,
    onReportStateUpdate: updateReportState,
    updateVisit,
    isTreeVisible: ({ treeKey: nextTreeKey }) => effectiveQuestionTreeKeys.includes(nextTreeKey),
  });

  // If URL points to a disabled tree, move to first enabled tree.
  useEffect(() => {
    if (!survey || !treeKey || !orgId || !shortId) return;
    if (effectiveQuestionTreeKeys.length === 0) return;
    if (effectiveQuestionTreeKeys.includes(treeKey)) return;

    const firstEnabled = first(effectiveQuestionTreeKeys);
    if (!firstEnabled) return;
    navigate(`${patientBasePath}/${orgId}/${shortId}/${firstEnabled}${location.search}`);
  }, [survey?.id, treeKey, orgId, shortId, patientBasePath, location.search, `${effectiveQuestionTreeKeys}`]);

  // ==================== EFFECTS ====================

  // Sync curTreeKey with URL treeKey
  useEffect(() => {
    if (survey && treeKey && surveyState.curTreeKey !== treeKey) {
      updateSurveyState((state) => ({
        ...state,
        curTreeKey: treeKey,
      }));
    }
  }, [survey, treeKey, surveyState.curTreeKey, updateSurveyState]);

  // Handle AID changes (reset state if AID changes)
  useEffect(() => {
    if (urlAid && currentAid && urlAid !== currentAid) {
      console.log('AID changed, resetting state');
      resetAllStates();
      setCurrentAid(urlAid);
      localStorage.setItem('currentAid', urlAid);
    } else if (urlAid && !currentAid) {
      setCurrentAid(urlAid);
      localStorage.setItem('currentAid', urlAid);
    }
  }, [urlAid, currentAid, resetAllStates]);

  // Restore state from visit data
  useEffect(() => {
    if (visit?.surveyState) {
      const savedReportState = visit.surveyState.reportState;
      const savedSurveyState = visit.surveyState.surveyState;

      if (savedReportState) {
        setReportState((prev) => ({
          ...(() => {
            const merged = {
              ...prev,
              ...savedReportState,
            };

            const serverTreeStates = Array.isArray(savedReportState.treeStates)
              ? savedReportState.treeStates
              : [];
            const localTreeStates = Array.isArray(prev.treeStates) ? prev.treeStates : [];

            // Keep local in-progress answers when server payload is still empty/stale.
            if (localTreeStates.length > 0 && serverTreeStates.length === 0) {
              merged.treeStates = prev.treeStates;
              merged.globalAnswerMap = prev.globalAnswerMap;
              merged.systemValueMap = prev.systemValueMap;
              merged.nextTreeKeys = prev.nextTreeKeys;
              merged.presentIllness = prev.presentIllness;
            }

            return merged;
          })(),
        }));
      }

      if (savedSurveyState) {
        setSurveyState((prev) => ({
          ...prev,
          ...savedSurveyState,
        }));
      }
    }
  }, [visit?.surveyState]);

  // ==================== RETURN ====================

  return {
    // Data state
    dataState,
    reportState,
    surveyState,
    errorState,
    isLoading: isVisitLoading,

    // Setters
    setReportState: updateReportState as React.Dispatch<React.SetStateAction<ReportStateType>>,
    setSurveyState: updateSurveyState as React.Dispatch<React.SetStateAction<SurveyStateType>>,
    setErrorState,

    // Navigation
    navigation: navigationProps,

    // Athena Integration
    athena,

    // Descriptive Note
    descriptiveNote,

    // Computed values
    surveyId,
    curTreeIdx,
    isLastTree,
    patientDetails,
    visit,
    survey: effectiveSurvey,
    org,
    defaultLanguage,
    treeByKey: treeByKeyData,
    treeSearchResults: treeSearchResultsData || [],

    // Helper functions
    updateVisit,
    resetAllStates,
    recordUnhandledError,
  };
}

// Create the container
const SurveyContainer = createContainer(useSurveyContainer);

export default SurveyContainer;
